import { useState, type ReactNode } from 'react';

/**
 * Discord-flavored markdown renderer for embed title / description / field values.
 *
 * Supports (in priority order):
 * - ```code blocks``` (with optional language tag)
 * - `inline code`
 * - ***bold italic***, **bold**, *italic*, _italic_
 * - __underline__, ~~strikethrough~~, ||spoiler|| (click to reveal)
 * - [label](url) hyperlinks (embeds only)
 * - <@id>, <@!id>, <#id>, <@&id> mentions
 * - <:name:id> and <a:name:id> custom emoji (rendered as :name:)
 * - Line-level: # H1, ## H2, ### H3, > blockquote, -# subtext
 * - `- item` / `* item` unordered lists; `1. item` ordered lists (indent for nesting)
 *
 * Pass `inline` to skip block-level elements (lists, headings, quotes) — useful
 * for embed titles where Discord only honors inline formatting.
 *
 * Not supported:
 * - Tables, footnotes, raw HTML (Discord doesn't render these either)
 * - Nested code blocks (Discord limitation)
 */
export function DiscordMarkdown({
  text,
  inline = false,
}: {
  text: string;
  inline?: boolean;
}): ReactNode {
  if (!text) return null;

  // Extract code blocks first so their content is left alone.
  const { stripped, blocks } = extractCodeBlocks(text);

  // Inline mode (used for titles / field names) — single-line, no block elements.
  if (inline) {
    return <>{renderInline(stripped.replace(/\n/g, ' '), blocks)}</>;
  }

  const lines = stripped.split('\n');
  return <>{lines.map((line, i) => renderLineBlock(line, blocks, i))}</>;
}

interface CodeBlock {
  lang?: string;
  body: string;
}

function extractCodeBlocks(text: string): { stripped: string; blocks: CodeBlock[] } {
  const blocks: CodeBlock[] = [];
  // Match ```optional-lang\nbody\n``` or ```body``` (single-line).
  const stripped = text.replace(
    /```(\w*)\n?([\s\S]*?)```/g,
    (_match, lang: string, body: string) => {
      const idx = blocks.length;
      blocks.push({ lang: lang || undefined, body });
      return ` CODEBLOCK${idx} `;
    },
  );
  return { stripped, blocks };
}

function renderLineBlock(line: string, blocks: CodeBlock[], key: number): ReactNode {
  // Standalone code block placeholder on its own line.
  const blockMatch = line.match(/^ CODEBLOCK(\d+) $/);
  if (blockMatch) {
    const block = blocks[Number(blockMatch[1])];
    if (block) {
      return (
        <pre
          key={key}
          className="my-1 overflow-x-auto rounded-[4px] bg-[#2b2d31] p-2 font-mono text-[12px] text-[#dcddde]"
        >
          <code>{block.body}</code>
        </pre>
      );
    }
  }

  // Empty line acts as a vertical spacer (Discord shows blank lines).
  if (line === '') {
    return <div key={key} className="h-2" />;
  }

  // -# subtext (small grey text). Per spec, allow up to 3 leading spaces.
  const subtextMatch = line.match(/^ {0,3}-# (.*)$/);
  if (subtextMatch) {
    return (
      <div key={key} className="text-[12px] leading-[1.3] text-[#949ba4]">
        {renderInline(subtextMatch[1]!, blocks)}
      </div>
    );
  }
  // Headings — CommonMark allows up to 3 leading spaces, and the `#`
  // sequence must be followed by a space. Discord supports H1, H2, H3
  // (more `#`s would be H4+ which Discord doesn't render specially).
  // Sizes match the actual Discord client more closely than before:
  //   H1 ≈ 24px bold, H2 ≈ 20px bold, H3 ≈ 16px semibold
  const h3 = line.match(/^ {0,3}### (.*)$/);
  if (h3) {
    return (
      <div key={key} className="mt-2 text-[16px] font-semibold leading-snug text-white">
        {renderInline(h3[1]!, blocks)}
      </div>
    );
  }
  const h2 = line.match(/^ {0,3}## (.*)$/);
  if (h2) {
    return (
      <div key={key} className="mt-3 text-[20px] font-bold leading-snug text-white">
        {renderInline(h2[1]!, blocks)}
      </div>
    );
  }
  const h1 = line.match(/^ {0,3}# (.*)$/);
  if (h1) {
    return (
      <div key={key} className="mt-3 text-[24px] font-bold leading-snug text-white">
        {renderInline(h1[1]!, blocks)}
      </div>
    );
  }
  // > blockquote
  const quote = line.match(/^ {0,3}> (.*)$/);
  if (quote) {
    return (
      <div key={key} className="flex gap-2 text-[#dcddde]">
        <span aria-hidden className="w-1 shrink-0 rounded bg-[#4f545c]" />
        <div className="flex-1">{renderInline(quote[1]!, blocks)}</div>
      </div>
    );
  }
  // Unordered list: `- item` or `* item` (require trailing space to avoid colliding with bold *foo*).
  const ulMatch = line.match(/^(\s*)[-*] (.*)$/);
  if (ulMatch) {
    const depth = Math.floor((ulMatch[1]?.length ?? 0) / 2);
    return (
      <div key={key} className="flex gap-1.5" style={{ paddingLeft: `${0.5 + depth * 1}rem` }}>
        <span className="select-none text-[#a3a6aa]">•</span>
        <span className="flex-1">{renderInline(ulMatch[2]!, blocks)}</span>
      </div>
    );
  }
  // Ordered list: `1. item`.
  const olMatch = line.match(/^(\s*)(\d+)\. (.*)$/);
  if (olMatch) {
    const depth = Math.floor((olMatch[1]?.length ?? 0) / 2);
    return (
      <div key={key} className="flex gap-1.5" style={{ paddingLeft: `${0.5 + depth * 1}rem` }}>
        <span className="select-none text-[#a3a6aa]">{olMatch[2]}.</span>
        <span className="flex-1">{renderInline(olMatch[3]!, blocks)}</span>
      </div>
    );
  }

  // Plain text line
  return <div key={key}>{renderInline(line, blocks)}</div>;
}

// Inline-level markdown. Operates recursively on captured groups.
function renderInline(text: string, blocks: CodeBlock[]): ReactNode {
  const out: ReactNode[] = [];
  let key = 0;
  let i = 0;

  while (i < text.length) {
    const remaining = text.slice(i);

    // Inline code block placeholder
    const blockMatch = remaining.match(/^ CODEBLOCK(\d+) /);
    if (blockMatch) {
      const block = blocks[Number(blockMatch[1])];
      if (block) {
        out.push(
          <code
            key={key++}
            className="rounded bg-[#2b2d31] px-1 font-mono text-[12px] text-[#dcddde]"
          >
            {block.body}
          </code>,
        );
        i += blockMatch[0].length;
        continue;
      }
    }

    // Inline code `...`
    const codeMatch = remaining.match(/^`([^`\n]+)`/);
    if (codeMatch) {
      out.push(
        <code
          key={key++}
          className="rounded bg-[#2b2d31] px-1 font-mono text-[12px] text-[#dcddde]"
        >
          {codeMatch[1]}
        </code>,
      );
      i += codeMatch[0].length;
      continue;
    }

    // ***bold italic***
    const biMatch = remaining.match(/^\*\*\*([^*\n]+)\*\*\*/);
    if (biMatch) {
      out.push(
        <em key={key++}>
          <strong>{renderInline(biMatch[1]!, blocks)}</strong>
        </em>,
      );
      i += biMatch[0].length;
      continue;
    }
    // **bold**
    const boldMatch = remaining.match(/^\*\*([^*\n]+)\*\*/);
    if (boldMatch) {
      out.push(<strong key={key++}>{renderInline(boldMatch[1]!, blocks)}</strong>);
      i += boldMatch[0].length;
      continue;
    }
    // *italic*
    const italicStarMatch = remaining.match(/^\*([^*\n]+)\*/);
    if (italicStarMatch) {
      out.push(<em key={key++}>{renderInline(italicStarMatch[1]!, blocks)}</em>);
      i += italicStarMatch[0].length;
      continue;
    }
    // __underline__ (check before single-underscore italic so it has priority)
    const underlineMatch = remaining.match(/^__([^_\n]+)__/);
    if (underlineMatch) {
      out.push(<u key={key++}>{renderInline(underlineMatch[1]!, blocks)}</u>);
      i += underlineMatch[0].length;
      continue;
    }
    // _italic_
    const italicUnderMatch = remaining.match(/^_([^_\n]+)_/);
    if (italicUnderMatch) {
      out.push(<em key={key++}>{renderInline(italicUnderMatch[1]!, blocks)}</em>);
      i += italicUnderMatch[0].length;
      continue;
    }
    // ~~strikethrough~~
    const strikeMatch = remaining.match(/^~~([^~\n]+)~~/);
    if (strikeMatch) {
      out.push(<s key={key++}>{renderInline(strikeMatch[1]!, blocks)}</s>);
      i += strikeMatch[0].length;
      continue;
    }
    // ||spoiler||
    const spoilerMatch = remaining.match(/^\|\|([^|\n]+)\|\|/);
    if (spoilerMatch) {
      out.push(<Spoiler key={key++}>{renderInline(spoilerMatch[1]!, blocks)}</Spoiler>);
      i += spoilerMatch[0].length;
      continue;
    }
    // [label](url) link
    const linkMatch = remaining.match(/^\[([^\]\n]+)\]\(([^)\s]+)\)/);
    if (linkMatch) {
      out.push(
        <a
          key={key++}
          href={linkMatch[2]}
          target="_blank"
          rel="noreferrer"
          className="text-[#00b0f4] hover:underline"
        >
          {renderInline(linkMatch[1]!, blocks)}
        </a>,
      );
      i += linkMatch[0].length;
      continue;
    }
    // User mention <@id> or <@!id>
    const userMatch = remaining.match(/^<@!?(\d{17,20})>/);
    if (userMatch) {
      out.push(<Mention key={key++}>@user</Mention>);
      i += userMatch[0].length;
      continue;
    }
    // Channel mention <#id>
    const channelMatch = remaining.match(/^<#(\d{17,20})>/);
    if (channelMatch) {
      out.push(<Mention key={key++}>#channel</Mention>);
      i += channelMatch[0].length;
      continue;
    }
    // Role mention <@&id>
    const roleMatch = remaining.match(/^<@&(\d{17,20})>/);
    if (roleMatch) {
      out.push(<Mention key={key++}>@role</Mention>);
      i += roleMatch[0].length;
      continue;
    }
    // Custom emoji <a?:name:id>
    const emojiMatch = remaining.match(/^<a?:([^:]+):\d+>/);
    if (emojiMatch) {
      out.push(<span key={key++}>:{emojiMatch[1]}:</span>);
      i += emojiMatch[0].length;
      continue;
    }

    // No pattern matched. Consume one char and continue.
    out.push(remaining[0]);
    i += 1;
  }

  // Coalesce adjacent string nodes for cleaner output.
  return coalesceStrings(out);
}

function coalesceStrings(nodes: ReactNode[]): ReactNode[] {
  const result: ReactNode[] = [];
  let buffer = '';
  for (const node of nodes) {
    if (typeof node === 'string') {
      buffer += node;
    } else {
      if (buffer) {
        result.push(buffer);
        buffer = '';
      }
      result.push(node);
    }
  }
  if (buffer) result.push(buffer);
  return result;
}

function Mention({ children }: { children: ReactNode }): ReactNode {
  return (
    <span className="rounded bg-[#5865F2]/30 px-1 font-medium text-[#dee0fc] hover:bg-[#5865F2]/50">
      {children}
    </span>
  );
}

function Spoiler({ children }: { children: ReactNode }): ReactNode {
  const [revealed, setRevealed] = useState(false);
  return (
    <span
      onClick={(e) => {
        e.stopPropagation();
        setRevealed(true);
      }}
      className={
        revealed
          ? 'rounded bg-[#3c4046]/40 px-1'
          : 'cursor-pointer rounded bg-[#202225] px-1 text-[#202225] hover:bg-[#3c4046]'
      }
      title={revealed ? undefined : 'Click to reveal spoiler'}
    >
      {children}
    </span>
  );
}
